import { memo, useCallback, useEffect, useState } from 'react';
import { Card } from '../../../../components/card/card';
import { Divider } from '../../../../components/divider/divider';
import Icon from '../../../../components/icons/icon';
import { Typography } from '../../../../components/typography/typography';
import '../../inventory-add-edit.scss';
import './product-pricing.scss';
import { ProductPricingType } from '../../hooks/use-add-product-data';
import { displayMoneyFormat } from '../../../../util/util';
import { SelectForm } from '../../../../components/select-form/select-form';
import { Location } from '../../../../clients/quipli-service/location/location';
import { toast } from '../../../../util/toasts';
import { DeleteModal } from '../../inventory-add-edit';
import SkeletonLoader from '../../../../components/skeleton-loader/skeleton-loader';

export const ProductPricing = ({
  productPricing,
  setProductPricing,
  isInitiallyLoading,
  isSaving,
  currency,
  allLocations,
  selectedLocationIds,
  deletePriceCall,
  type,
}: {
  productPricing: ProductPricingType[];
  setProductPricing: (value: ProductPricingType) => void;
  isInitiallyLoading: boolean;
  isSaving: boolean;
  currency: string;
  allLocations: Location[];
  selectedLocationIds: number[];
  deletePriceCall: (id: string, row: number) => void;
  type: 'Part' | 'Service';
}) => {
  return (
    <Card className="card flex flex-col gap-4 w-auto min-w-[auto] justify-around">
      <div className="part-inventory-title-card">
        <Typography type="text-xl" weight="semi-bold">
          Pricing
        </Typography>
        <Divider />
      </div>

      {isInitiallyLoading ? (
        <SkeletonLoader style={{ width: '--webkit-fill-available', height: '24vh' }} />
      ) : (
        <PricingTable
          productPricing={productPricing}
          setProductPricing={setProductPricing}
          currency={currency}
          allLocations={allLocations}
          isSaving={isSaving}
          selectedLocationIds={selectedLocationIds}
          deletePriceCall={deletePriceCall}
          type={type}
        />
      )}
    </Card>
  );
};

const PricingTable = ({
  productPricing,
  setProductPricing,
  currency,
  isSaving,
  allLocations,
  selectedLocationIds,
  deletePriceCall,
  type,
}: {
  productPricing: ProductPricingType[];
  setProductPricing: (value: ProductPricingType) => void;
  currency: string;
  isSaving: boolean;
  allLocations: Location[];
  selectedLocationIds: number[];
  deletePriceCall: (id: string, row: number) => void;
  type: 'Part' | 'Service';
}) => {
  const [editingRow, setEditingRow] = useState<number | null>(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [rowToDelete, setRowToDelete] = useState<number | null>(null);

  const isMakingNewRow = editingRow === productPricing.length;

  const maxCols = 4;

  const updatePartPricingField = useCallback(
    (newData: ProductPricingType) => {
      setProductPricing(newData);
    },
    [setProductPricing],
  );

  const locationOptions: { value: number | undefined; label: string }[] = [
    { value: undefined, label: 'All Locations' },
    ...allLocations.map((location) => {
      return { value: location.id, label: location.name };
    }),
  ];

  const remainingLocationOptions = locationOptions.filter(
    (option) => !productPricing.map((pp) => pp.locationId).includes(option.value),
  );

  return (
    <div className="w-full overflow-x-auto max-w-full grid">
      <DeleteModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onDelete={async () => {
          if (rowToDelete !== null) {
            await deletePriceCall(productPricing[rowToDelete].id, rowToDelete);
          }
          setIsDeleteModalOpen(false);
        }}
        productType="Price"
        isDeleting={false}
      />
      <table className="pretty-table ml-0 mr-0 md:ml-8 md:mr-8">
        <thead className="max-w-full">
          <tr>
            <th colSpan={maxCols}>
              <Typography type="text-xl" className="table-title" weight="medium">
                {type} Pricing
              </Typography>
            </th>
          </tr>
          <tr>
            <th>Type</th>
            <th>Price</th>
            <th>Locations</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {productPricing.map((pricing, index) => (
            <MemoizedConditionalRow
              pricing={pricing}
              editingRow={editingRow}
              setEditingRow={setEditingRow}
              row={index}
              setValue={updatePartPricingField}
              isSaving={isSaving}
              currency={currency}
              allLocations={allLocations}
              remainingLocationOptions={remainingLocationOptions} // TODO: simplify these params and the duplication of these components
              onDeletePress={() => {
                setRowToDelete(index);
                setIsDeleteModalOpen(true);
              }}
            />
          ))}
          {isMakingNewRow && (
            <MemoizedConditionalRow
              pricing={{ id: '', type: 'Sale', price: 0, locationId: -1 }}
              editingRow={editingRow}
              setEditingRow={setEditingRow}
              row={productPricing.length}
              setValue={(newData) => {
                setProductPricing(newData);
                setEditingRow(null);
              }}
              isSaving={isSaving}
              currency={currency}
              allLocations={allLocations}
              remainingLocationOptions={remainingLocationOptions} // TODO: simplify these params and the duplication of these components
              onDeletePress={() => {
                setRowToDelete(null);
                setIsDeleteModalOpen(true);
              }}
            />
          )}
        </tbody>
        <tfoot>
          <tr>
            <th colSpan={maxCols}>
              {selectedLocationIds == null || selectedLocationIds.length === 0 ? (
                <Typography type="text-md">
                  Select at least one location above to add pricing information for this part.
                </Typography>
              ) : (
                <div
                  className="flex flex-row gap-2 cursor-pointer"
                  onClick={() => setEditingRow(productPricing.length)}
                >
                  <Icon icon="PlusCircle" size="medium" stroke="var(--quipli-black-800)" />
                  <Typography type="text-md">Add sale price</Typography>
                </div>
              )}
            </th>
          </tr>
        </tfoot>
      </table>
    </div>
  );
};

const ConditionalRow = ({
  pricing,
  editingRow,
  setEditingRow,
  row,
  setValue,
  isSaving,
  currency,
  allLocations,
  remainingLocationOptions,
  onDeletePress,
}: {
  pricing: ProductPricingType;
  editingRow: number | null;
  setEditingRow: (value: React.SetStateAction<number | null>) => void;
  row: number;
  setValue: (newData: ProductPricingType) => void;
  isSaving: boolean;
  currency: string;
  allLocations: Location[];
  remainingLocationOptions: { value: number | undefined; label: string }[];
  onDeletePress: () => void;
}) => {
  const [unsavedInputValue, setUnsavedInputValue] = useState<ProductPricingType>({ ...pricing });

  useEffect(() => {
    setUnsavedInputValue(pricing);
  }, [pricing]);

  const updateUnsavedPartInventoryField = useCallback(
    (value: number | undefined, field: keyof ProductPricingType) => {
      setUnsavedInputValue((old) => {
        const newInputValues = { ...old };

        if (field === 'locationId') {
          newInputValues[field] = value;
        } else if (field === 'price') {
          newInputValues[field] = value as number;
        }
        return newInputValues;
      });
    },
    [setUnsavedInputValue],
  );

  const location = allLocations.find((l) => l.id === unsavedInputValue.locationId);

  let remainingOptionsAndEditing = remainingLocationOptions;
  if (unsavedInputValue.locationId !== -1) {
    remainingOptionsAndEditing = [
      { value: unsavedInputValue.locationId, label: location?.name ?? '' },
      ...remainingLocationOptions,
    ];
  }

  return (
    <tr>
      <td>
        <Typography type="text-md">Sale</Typography>
      </td>
      <td>
        <MemoizedConditionalInput
          value={unsavedInputValue.price}
          setValue={(value) => updateUnsavedPartInventoryField(value, 'price')}
          editingRow={editingRow}
          row={row}
          isSaving={isSaving}
          isCurrency={true}
          currency={currency}
        />
      </td>
      <td>
        {/* TODO: make sure all the modals are scrollable/have a higher z value */}
        {editingRow === row ? (
          <SelectForm
            value={{
              value: unsavedInputValue.locationId,
              label: location?.name
                ? location?.name
                : unsavedInputValue.locationId === undefined
                ? 'All Locations'
                : '',
            }}
            options={remainingOptionsAndEditing}
            onChange={(s) => updateUnsavedPartInventoryField(s?.value ?? undefined, 'locationId')}
            isDisabled={isSaving}
            className="select"
          />
        ) : (
          <Typography type="text-md">{location?.name ?? 'All Locations'}</Typography>
        )}
      </td>

      <td>
        {editingRow !== row ? (
          <div className="flex flex-row gap-1">
            <div
              className="part-inv-edit-button"
              onClick={() => {
                if (isSaving) {
                  return;
                }
                if (editingRow !== null) {
                  toast('Please save or cancel your changes before editing another row', 'warning');
                } else {
                  setEditingRow(row);
                }
              }}
            >
              <Icon icon="Edit" />
            </div>
            <div
              className="part-inv-edit-button"
              onClick={() => {
                if (isSaving) {
                  return;
                }
                if (editingRow !== null) {
                  toast('Please save or cancel your changes before deleting another row', 'warning');
                } else {
                  onDeletePress();
                }
              }}
            >
              <Icon icon="Trash" />
            </div>
          </div>
        ) : (
          <div className="part-inv-edit-button">
            <Icon
              icon="Check"
              onClick={() => {
                if (isSaving) {
                  return;
                }
                setValue(unsavedInputValue);
                setEditingRow(null);
              }}
            />
            <Icon
              icon="Close"
              onClick={() => {
                if (isSaving) {
                  return;
                }
                setUnsavedInputValue(pricing);
                setEditingRow(null);
              }}
            />
          </div>
        )}
      </td>
    </tr>
  );
};

const ConditionalInput = ({
  editingRow,
  row,
  value,
  setValue,
  isSaving,
  isCurrency = false,
  currency = 'USD',
}: {
  editingRow: number | null;
  row: number;
  value: number;
  setValue: (value: number) => void;
  isSaving: boolean;
  isCurrency?: boolean;
  currency?: string;
}) => {
  return (
    <div className="conditional-input-wrapper">
      {editingRow === row ? (
        <input
          value={value}
          className="conditional-input"
          type="number"
          onWheel={(e) => e.currentTarget.blur()}
          onChange={(e) => setValue(parseFloat(e.target.value))}
          disabled={isSaving}
        />
      ) : (
        <Typography className="conditional-input" type="text-md">
          {isCurrency ? displayMoneyFormat(value, currency) : value}
        </Typography>
      )}
    </div>
  );
};
const MemoizedConditionalRow = memo(ConditionalRow);
const MemoizedConditionalInput = memo(ConditionalInput);
